import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, IconButton, Message, Panel, SelectPicker, Stack } from 'rsuite';

import { useCloseDates, useUsers, useUserWorkplan, useUserTimeOffDays } from '../hooks/useQueries';
import { formatHours } from '../utils/chartUtils';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const addMonthsIso = (monthStart, deltaMonths) => {
  if (!isIsoDate(monthStart)) return null;
  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;
  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  d.setHours(0, 0, 0, 0);
  return toMonthStartIso(d);
};

const snapToHalfYearStartIso = (monthStartIso) => {
  if (!isIsoDate(monthStartIso)) return null;
  const y = String(monthStartIso).slice(0, 4);
  const m = Number(String(monthStartIso).slice(5, 7));
  if (!Number.isInteger(m) || m < 1 || m > 12) return null;
  const startMonth = m <= 6 ? '01' : '07';
  return `${y}-${startMonth}-01`;
};

// Same utilization math as ChartsPage:
// workableHours = weekdays*8 - closeDates*8; then scaled by user ftePercent.
const countWeekdaysInclusive = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  if (a.getTime() >= b.getTime()) return 0;

  let count = 0;
  const cur = new Date(a);
  while (cur.getTime() < b.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (start, endExclusive, rows) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (a.getTime() >= b.getTime()) return 0;

  const years = new Set();
  for (let y = a.getFullYear(); y <= b.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= a.getTime() && d.getTime() < b.getTime()) {
        count += 1;
      }
    }
  }
  return count;
};

const TIME_OFF_HOURS_PER_DAY = 8;

export default function MyWorkplanPage() {
  const WINDOW_SIZE = 6;
  const WORKPLAN_SELECTED_USER_STORAGE_KEY = 'ppc.myWorkplan.userId';

  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: closeDates = [] } = useCloseDates();

  const [selectedUserId, setSelectedUserId] = useState(() => {
    try {
      const raw = localStorage.getItem(WORKPLAN_SELECTED_USER_STORAGE_KEY);
      const n = Number(raw);
      return Number.isInteger(n) ? n : null;
    } catch {
      return null;
    }
  });
  const [startMonth, setStartMonth] = useState(() => {
    const initial = toMonthStartIso(new Date());
    return snapToHalfYearStartIso(initial) ?? initial;
  });

  useEffect(() => {
    try {
      if (Number.isInteger(selectedUserId)) {
        localStorage.setItem(WORKPLAN_SELECTED_USER_STORAGE_KEY, String(selectedUserId));
      } else {
        localStorage.removeItem(WORKPLAN_SELECTED_USER_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, [selectedUserId]);

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonthsIso(startMonth, i);
      if (ms) list.push(ms);
    }
    return list;
  }, [WINDOW_SIZE, startMonth]);

  const monthRangeLabel = useMemo(() => {
    if (months.length === 0) return '';
    const first = formatMonthLabel(months[0]);
    const last = formatMonthLabel(months[months.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [months]);

  const currentMonthStart = useMemo(() => {
    const current = toMonthStartIso(new Date());
    return snapToHalfYearStartIso(current) ?? current;
  }, []);
  const isOnCurrentWindow = startMonth === currentMonthStart;

  const userList = useMemo(() => {
    const list = Array.isArray(users) ? users : users?.users;
    const arr = Array.isArray(list) ? list : [];
    return arr
      .map((u) => ({
        id: Number(u?.id),
        name: String(u?.name || u?.displayName || u?.email || `User ${u?.id}`),
        ftePercent: Number(u?.ftePercent ?? u?.fte_percent ?? 100),
      }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [users]);

  const selectedUser = useMemo(
    () => userList.find((u) => Number(u?.id) === Number(selectedUserId)) || null,
    [selectedUserId, userList]
  );

  const selectedUserFteScale = useMemo(() => {
    const raw = Number(selectedUser?.ftePercent);
    const fte = Number.isFinite(raw) ? raw : 100;
    return Math.max(0, fte / 100);
  }, [selectedUser?.ftePercent]);

  const userPickerData = useMemo(() => userList.map((u) => ({ label: u.name, value: u.id })), [userList]);

  const {
    data: workplan,
    isLoading: workplanLoading,
    error: workplanError,
  } = useUserWorkplan(Number.isInteger(selectedUserId) ? selectedUserId : null, months);

  const {
    data: myTimeOff,
    isLoading: myTimeOffLoading,
    error: myTimeOffError,
  } = useUserTimeOffDays(Number.isInteger(selectedUserId) ? selectedUserId : null);

  const upcomingTimeOffDays = useMemo(() => {
    const raw = Array.isArray(myTimeOff?.time_off_days)
      ? myTimeOff.time_off_days
      : Array.isArray(myTimeOff?.vacation_days)
        ? myTimeOff.vacation_days
        : [];

    return Array.from(
      new Set(
        raw
          .map((d) => String(d || '').slice(0, 10))
          .filter((d) => isIsoDate(d))
      )
    ).sort((a, b) => String(a).localeCompare(String(b)));
  }, [myTimeOff]);

  const formatDateLabel = (isoDate) => {
    if (!isIsoDate(isoDate)) return String(isoDate || '');
    const y = Number(isoDate.slice(0, 4));
    const m = Number(isoDate.slice(5, 7));
    const d = Number(isoDate.slice(8, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const rows = useMemo(() => {
    const r = Array.isArray(workplan?.rows) ? workplan.rows : [];
    return [...r].sort((a, b) => {
      const pa = String(a?.projectName || '');
      const pb = String(b?.projectName || '');
      if (pa !== pb) return pa.localeCompare(pb);
      const ta = String(a?.taskName || '');
      const tb = String(b?.taskName || '');
      if (ta !== tb) return ta.localeCompare(tb);
      return String(a?.activityName || '').localeCompare(String(b?.activityName || ''));
    });
  }, [workplan?.rows]);

  const displayRows = useMemo(() => {
    const out = [];
    let lastTaskKey = null;

    for (const r of rows) {
      const projectKey = r?.projectId ?? r?.projectName ?? '';
      const taskKey = r?.taskId ?? r?.taskName ?? '';
      const groupKey = `${String(projectKey)}|${String(taskKey)}`;

      const showTaskLabel = groupKey !== lastTaskKey;
      out.push({ ...r, __showTaskLabel: showTaskLabel, __taskGroupKey: groupKey });
      lastTaskKey = groupKey;
    }

    return out;
  }, [rows]);

  const totalsByMonth = useMemo(() => {
    const totals = {};
    for (const ms of months) totals[ms] = 0;
    for (const r of rows) {
      for (const ms of months) {
        totals[ms] += Number(r?.hours?.[ms] ?? 0);
      }
    }
    return totals;
  }, [months, rows]);

  const utilizationByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) {
      if (!isIsoDate(ms)) {
        out[ms] = 0;
        continue;
      }

      const y = Number(ms.slice(0, 4));
      const m0 = Number(ms.slice(5, 7)) - 1;
      if (!Number.isInteger(y) || !Number.isInteger(m0)) {
        out[ms] = 0;
        continue;
      }

      const start = new Date(y, m0, 1);
      start.setHours(0, 0, 0, 0);
      const endExclusive = new Date(y, m0 + 1, 1);
      endExclusive.setHours(0, 0, 0, 0);

      const weekdays = countWeekdaysInclusive(start, endExclusive);
      const closeCount = countCloseDatesInRange(start, endExclusive, closeDates);
      const workable = Math.max(0, weekdays * 8 - closeCount * 8) * selectedUserFteScale;

      const hours = Number(totalsByMonth?.[ms] ?? 0);
      if (!Number.isFinite(workable) || workable <= 0) {
        out[ms] = 0;
      } else if (!Number.isFinite(hours) || hours <= 0) {
        out[ms] = 0;
      } else {
        out[ms] = (hours / workable) * 100;
      }
    }
    return out;
  }, [closeDates, months, selectedUserFteScale, totalsByMonth]);

  const timeOffHoursByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) out[ms] = 0;

    for (const day of upcomingTimeOffDays) {
      if (!isIsoDate(day)) continue;
      const monthStart = `${day.slice(0, 7)}-01`;
      if (!Object.prototype.hasOwnProperty.call(out, monthStart)) continue;
      out[monthStart] += TIME_OFF_HOURS_PER_DAY;
    }

    return out;
  }, [months, upcomingTimeOffDays]);

  const colTemplate = useMemo(() => {
    // Match actuals grid styling: tighter month columns, a bit more space for Activity.
    const monthCols = months.map(() => 'minmax(110px, 1fr)').join(' ');
    return `minmax(220px, 1.2fr) minmax(260px, 1.4fr) ${monthCols}`.trim();
  }, [months]);

  const goPrevMonths = () => {
    const ns = addMonthsIso(startMonth, -WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goNextMonths = () => {
    const ns = addMonthsIso(startMonth, WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goCurrentMonths = () => {
    setStartMonth(currentMonthStart);
  };

  return (
    <div>
      <Panel
        bordered
        header={
          <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
            <Stack spacing={10} alignItems="center" wrap>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, lineHeight: '22px' }}>My Workplan</h3>
                </div>
              </div>

              <SelectPicker
                data={userPickerData}
                value={selectedUserId}
                onChange={(v) => setSelectedUserId(v ?? null)}
                placeholder={usersLoading ? 'Loading users…' : 'Select a user'}
                style={{ minWidth: 260 }}
                cleanable
                searchable
              />
            </Stack>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                aria-label="Previous months"
                title="Previous months"
                onClick={goPrevMonths}
              />
              <div
                style={{
                  color: '#666',
                  fontSize: 12,
                  fontWeight: 700,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {monthRangeLabel}
              </div>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                aria-label="Next months"
                title="Next months"
                onClick={goNextMonths}
              />
              <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
                Current
              </Button>
            </div>
          </Stack>
        }
      >
        {!Number.isInteger(selectedUserId) ? (
          <Message type="info" showIcon>
            Select a user to view forecast hours.
          </Message>
        ) : workplanError ? (
          <Message type="error" showIcon>
            Failed to load workplan: {workplanError?.message || String(workplanError)}
          </Message>
        ) : null}

        <div style={{ overflowX: 'auto', paddingBottom: 6, marginTop: 10 }}>
          <div
            style={{
              minWidth: 980,
              display: 'grid',
              gridTemplateColumns: colTemplate,
              gap: 0,
              alignItems: 'stretch',
            }}
          >
            <div style={{ fontWeight: 800, padding: '10px 10px', borderBottom: '1px solid #e6e6e6' }}>
              Task Name
            </div>
            <div style={{ fontWeight: 800, padding: '10px 10px', borderBottom: '1px solid #e6e6e6' }}>
              Activity
            </div>
            {months.map((m) => (
              <div
                key={m}
                style={{
                  padding: '10px 10px',
                  borderBottom: '1px solid #e6e6e6',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <div style={{ fontWeight: 800 }}>{formatMonthLabel(m)}</div>
              </div>
            ))}

            {workplanLoading ? (
              <div style={{ gridColumn: `1 / span ${2 + months.length}`, padding: 16, color: '#666' }}>Loading workplan…</div>
            ) : rows.length === 0 && Number.isInteger(selectedUserId) ? (
              <div style={{ gridColumn: `1 / span ${2 + months.length}`, padding: 16, color: '#666' }}>No forecast hours in this window.</div>
            ) : (
              displayRows.map((r) => {
                const taskLabel = r?.projectName
                  ? `${r.projectName} / ${r.taskName || ''}`.trim()
                  : String(r?.taskName || '');

                return (
                  <div key={`${r.taskId}|${r.activityId}`} style={{ display: 'contents' }}>
                    <div
                      style={{
                        padding: '10px 10px',
                        borderBottom: '1px solid #f0f0f0',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '16px',
                        fontWeight: r.__showTaskLabel ? 700 : 400,
                        color: r.__showTaskLabel ? '#222' : '#999',
                      }}
                      title={r.__showTaskLabel ? taskLabel : ''}
                    >
                      {r.__showTaskLabel && Number.isInteger(Number(r?.taskId)) ? (
                        <Link
                          to={`/tasks/${Number(r.taskId)}`}
                          state={{ backTo: '/my-workplan' }}
                          style={{
                            color: 'var(--rs-primary-500)',
                            textDecoration: 'underline',
                            display: 'block',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                          title={taskLabel}
                        >
                          {taskLabel}
                        </Link>
                      ) : r.__showTaskLabel ? (
                        taskLabel
                      ) : (
                        ''
                      )}
                    </div>
                    <div
                      style={{
                        padding: '10px 10px',
                        borderBottom: '1px solid #f0f0f0',
                        minWidth: 0,
                        display: 'block',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '16px',
                        color: '#555',
                      }}
                      title={r?.activityName || ''}
                    >
                      {r?.activityName || '—'}
                    </div>
                    {months.map((ms) => {
                      const n = Number(r?.hours?.[ms] ?? 0);
                      return (
                        <div
                          key={`${r.taskId}|${r.activityId}|${ms}`}
                          style={{
                            padding: '10px 10px',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontVariantNumeric: 'tabular-nums',
                            color: n === 0 ? '#999' : '#222',
                          }}
                        >
                          {formatHours(n)}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}

            {Number.isInteger(selectedUserId) && months.length > 0 ? (
              <>
                <div style={{ display: 'contents' }}>
                  <div
                    style={{
                      padding: '10px 10px',
                      borderTop: '2px solid #e6e6e6',
                      fontWeight: 800,
                    }}
                  >
                    Total
                  </div>

                  <div style={{ padding: '10px 10px', borderTop: '2px solid #e6e6e6' }} />

                  {months.map((ms) => (
                    <div
                      key={`totals|${ms}`}
                      style={{
                        padding: '10px 10px',
                        borderTop: '2px solid #e6e6e6',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 800,
                      }}
                    >
                      {formatHours(Number(totalsByMonth?.[ms] ?? 0))}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'contents' }}>
                  <div
                    style={{
                      padding: '10px 10px',
                      borderTop: '1px solid #f0f0f0',
                      fontWeight: 800,
                      color: '#555',
                    }}
                    title="Forecast hours / workable hours (after FTE reduction)"
                  >
                    Utilization
                  </div>
                  <div style={{ padding: '10px 10px', borderTop: '1px solid #f0f0f0' }} />
                  {months.map((ms) => (
                    <div
                      key={`util|${ms}`}
                      style={{
                        padding: '10px 10px',
                        borderTop: '1px solid #f0f0f0',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 800,
                        color: utilizationByMonth?.[ms] >= 100 ? '#cf1322' : '#595959',
                      }}
                      title="Forecast hours / workable hours (after FTE reduction)"
                    >
                      {`${Math.round(Number(utilizationByMonth?.[ms] ?? 0))}%`}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'contents' }}>
                  <div
                    style={{
                      padding: '10px 10px',
                      borderTop: '1px solid #f0f0f0',
                      fontWeight: 800,
                      color: '#555',
                    }}
                    title="Upcoming time off hours in this month"
                  >
                    Time Off
                  </div>
                  <div style={{ padding: '10px 10px', borderTop: '1px solid #f0f0f0' }} />
                  {months.map((ms) => (
                    <div
                      key={`timeoff|${ms}`}
                      style={{
                        padding: '10px 10px',
                        borderTop: '1px solid #f0f0f0',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 800,
                        color: '#595959',
                      }}
                      title="Upcoming time off hours in this month"
                    >
                      {myTimeOffLoading
                        ? '…'
                        : myTimeOffError
                          ? '—'
                          : formatHours(Number(timeOffHoursByMonth?.[ms] ?? 0))}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Panel>

      <Panel
        bordered
        style={{ marginTop: 12 }}
        header={<div style={{ fontWeight: 700 }}>Upcoming Time Off Days</div>}
      >
        {!Number.isInteger(selectedUserId) ? (
          <Message type="info" showIcon>
            Select a user to view upcoming time off days.
          </Message>
        ) : myTimeOffLoading ? (
          <div style={{ color: '#666' }}>Loading time off days…</div>
        ) : myTimeOffError ? (
          <Message type="warning" showIcon>
            Failed to load upcoming time off days.
          </Message>
        ) : upcomingTimeOffDays.length === 0 ? (
          <Message type="info" showIcon>
            No upcoming time off days.
          </Message>
        ) : (
          <Stack spacing={8} wrap>
            {upcomingTimeOffDays.map((day) => (
              <div
                key={day}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #e5e5ea',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                title={day}
              >
                {formatDateLabel(day)}
              </div>
            ))}
          </Stack>
        )}
      </Panel>
    </div>
  );
}
